
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  RefreshCw,
  CreditCard,
  CalendarDays,
  Package,
} from "lucide-react";

import {
  getPartnerSubscriptions,
  getActiveFeatureUsage,
} from "../services/PartnerService";
import "./admin.css";

const formatDate = (value) => {
  if (!value) return "—";

  const date = new Date(value);

  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleString("en-IN", {
        dateStyle: "medium",
        timeStyle: "short",
      });
};

const formatMoney = (value) => {
  const amount = Number(value ?? 0);

  return amount.toLocaleString("en-IN", {
    style: "currency",
    currency: "INR",
  });
};

const Detail = ({ label, value }) => (
  <div className="company-detail-field">
    <span>{label}</span>
   <strong>
  {value === null || value === undefined || value === ""
    ? "—"
    : value}
</strong>
  </div>
);

export default function SubscriptionDetailsPage() {
  const { partnerId } = useParams();
  const navigate = useNavigate();

  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const fetchSubscriptions = async () => {
  setLoading(true);
  setError("");

  try {
    // Load subscription purchases for the selected partner.
    const response = await getPartnerSubscriptions(partnerId);

    const purchases = Array.isArray(response.data)
      ? response.data
      : [];

    // These values come from the live subscription-usage API.
    const featureFields = [
      {
        featureCode: "RESUME_DOWNLOAD",
        field: "resumeUsed",
      },
      {
        featureCode: "JOB_POST",
        field: "jobPostUsed",
      },
      {
        featureCode: "JOB_INVITE",
        field: "jobInviteUsed",
      },
      {
        featureCode: "PROFILE_VIEW",
        field: "profileViewUsed",
      },
    ];

    const updatedPurchases = await Promise.all(
      purchases.map(async (purchase) => {
        // Usage cards should reflect the current active subscription.
        const isActive =
          String(purchase.status || "").toUpperCase() === "ACTIVE";

        const userId = purchase.userId ?? purchase.user_id;
        const userType = String(
          purchase.userType ?? purchase.user_type ?? "PARTNER"
        ).toUpperCase();

        if (!isActive || !userId) {
          return purchase;
        }

        const usageResults = await Promise.all(
          featureFields.map(async ({ featureCode, field }) => {
            try {
              const usageResponse = await getActiveFeatureUsage(
                userId,
                userType,
                featureCode
              );

              // PartnerService.js returns an Axios response.
              const usage =
                usageResponse?.data ?? usageResponse;

              return [
                field,
                Number(
                  usage?.usedCount ??
                  usage?.used_count ??
                  purchase[field] ??
                  0
                ),
              ];
            } catch (usageError) {
              console.error(
                `Unable to load ${featureCode} for user ${userId}:`,
                usageError
              );

              // Keep the purchase's stored value if live usage fails.
              return [field, purchase[field] ?? 0];
            }
          })
        );

        return {
          ...purchase,
          ...Object.fromEntries(usageResults),
        };
      })
    );

    setSubscriptions(updatedPurchases);
  } catch (err) {
    console.error("Subscription fetch error:", err);

    setError(
      "Unable to load subscription details. Please check the backend API."
    );
  } finally {
    setLoading(false);
  }
};
  useEffect(() => {
    fetchSubscriptions();
  }, [partnerId]);

  return (
    <div className="admin-page">
      <div className="admin-header">
        <div>
          <h2>Subscription Details</h2>
          {/* <p>Partner ID: {partnerId}</p> */}
        </div>

        <div className="page-bottom-actions">
          <button
            className="partner-detail-btn"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft size={16} />
            Back
          </button>

          <button
            className="partner-detail-btn subscription"
            onClick={fetchSubscriptions}
            disabled={loading}
          >
            <RefreshCw size={16} />
            Refresh
          </button>
        </div>
      </div>

      {loading ? (
        <div className="company-detail-card">
          Loading subscription details...
        </div>
      ) : error ? (
        <div className="company-detail-card">
          <p>{error}</p>
          <button
            className="partner-detail-btn"
            onClick={fetchSubscriptions}
          >
            Try Again
          </button>
        </div>
      ) : subscriptions.length === 0 ? (
        <div className="company-detail-card">
          <Package size={30} />
          <h3>No subscriptions found</h3>
          <p>No purchase records were returned for this user .</p>
        </div>
      ) : (
        subscriptions.map((item) => (
          <div className="company-detail-card" key={item.id}>
            <div className="company-detail-sections">
              <section className="company-detail-section">
                <h3>
                  <Package size={17} /> Plan Information
                </h3>

                <div className="company-detail-grid">
                  <Detail label="Purchase ID" value={item.id} />
                  <Detail label="Plan Name" value={item.planName} />
                  <Detail label="Plan Code" value={item.planCode} />
                  <Detail
                    label="Product ID"
                    value={item.subscriptionProductId}
                  />
                  <Detail label="User ID" value={item.userId} />
                  <Detail label="User Type" value={item.userType} />
                  <Detail label="Subscription Status" value={item.status} />
                </div>
              </section>

              <section className="company-detail-section">
                <h3>
                  <CreditCard size={17} /> Payment Details
                </h3>

                <div className="company-detail-grid">
                  <Detail label="Plan Price" value={formatMoney(item.price)} />
                  <Detail label="GST" value={formatMoney(item.gstAmount)} />
                  <Detail
                    label="Discount"
                    value={formatMoney(item.discountAmount)}
                  />
                  <Detail
                    label="Total Amount"
                    value={formatMoney(item.totalAmount)}
                  />
                  <Detail
                    label="Payment Status"
                    value={item.paymentStatus}
                  />
                  <Detail
                    label="Payment Method"
                    value={item.paymentMethod}
                  />
                  <Detail
                    label="Transaction ID"
                    value={item.transactionId}
                  />
                </div>
              </section>

              <section className="company-detail-section">
                <h3>
                  <CalendarDays size={17} /> Subscription Period
                </h3>

                <div className="company-detail-grid">
                  <Detail
                    label="Purchase Date"
                    value={formatDate(item.purchaseDate)}
                  />
                  <Detail
                    label="Start Date"
                    value={formatDate(item.startDate)}
                  />
                  <Detail
                    label="Expiry Date"
                    value={formatDate(item.expiryDate)}
                  />
                </div>
              </section>

              <section className="company-detail-section">
                <h3>Usage Limits</h3>

                <div className="company-detail-grid">
                  <Detail
                    label="Resume Downloads"
                    value={`${item.resumeUsed ?? 0} / ${item.resumeLimit ?? 0}`}
                  />
                  <Detail
                    label="Job Posts"
                    value={`${item.jobPostUsed ?? 0} / ${item.jobPostLimit ?? 0}`}
                  />
                  <Detail
                    label="Job Invites"
                    value={`${item.jobInviteUsed ?? 0} / ${item.jobInviteLimit ?? 0}`}
                  />
                  <Detail
                    label="Profile Views"
                    value={`${item.profileViewUsed ?? 0} / ${item.profileViewLimit ?? 0}`}
                  />
                </div>
              </section>
            </div>
          </div>
        ))
      )}
    </div>
  );
}
